import { useHistory } from 'react-router-dom';

enum Paths {
  SurveyDetail = 'surveyDetail',
  ArchivedSurveyDetail = 'archivedSurveyDetail',
  Surveys = 'surveys',
  PulserSurveys = 'pulser',
  PulserConversationSubjects = 'pulserConversationSubjects',
  CreateSurvey = 'createSurvey/TOPIC_GROUP',
  SurveyArchive = 'surveyArchive',
  ReportDetail = 'report',
  CreateOnboarding = 'createOnboarding',
  CreateProcessSurvey = 'createProcessSurvey',
  CreateExit = 'createExit',
  ConversationSubjects = 'conversationSubjects',
  Pricing = 'settings/pricing',
  Overview = 'overview',
  Onboarding = 'onboarding',
  ArchivedOnboarding = 'archivedOnboarding',
  ProcessSurveys = 'processSurveys',
  ProcessSurveyDetail = 'processSurvey',
  ArchivedProcessSurveyDetail = 'archivedProcessSurvey',
  TopicEditor = 'topicEditor',
  TopicEditorPreview = 'topicEditorPreview',
  Process = 'process',
  ReopenSurvey = 'reopenSurvey',
  Exit = 'exit',
  TransactionHistory = 'transactionHistory',
  Settings = 'settings',
}

const pathCreator = <T extends Array<string | undefined>>(createPath: (...param: T) => string, push) => ({
  toFunc: (...params: T) => push(createPath(...params)),
  createPath,
});

const addOptionalParam = (param?: string): string => (param ? `/${param}` : '');

/**
 * Surveys
 */
const createReopenSurveyPath = (id: string) => `/${Paths.ReopenSurvey}/${id}`;
const createSurveysPath = (status?: string) => `/${Paths.Surveys}${addOptionalParam(status)}`;
const createPulserSurveysPath = (status?: string) => `/${Paths.PulserSurveys}${addOptionalParam(status)}`;
const createPulserConversationSubjectsPath = () => `/${Paths.PulserConversationSubjects}`;
const createPulserTopicsPath = () => `/${Paths.PulserSurveys}/topics`;
const createSurveyDetailPath = (id: string, status?: string) =>
  `/${Paths.SurveyDetail}/${id}${addOptionalParam(status)}`;
const createArchivedSurveyDetailPath = (id: string, status?: string) =>
  `/${Paths.ArchivedSurveyDetail}/${id}${addOptionalParam(status)}`;
const createReportsSettingPath = (id: string) => `/${Paths.SurveyDetail}/${id}/results`;
const crateCreateSurveyPath = (id: string, title: string) => `/${Paths.CreateSurvey}/${id}/${title}`;
const createOnboardingPath = (surveyGroupId?: string, type?: string) =>
  `/${Paths.Onboarding}${addOptionalParam(surveyGroupId)}${addOptionalParam(type)}`;
const createExitPath = (surveyGroupId?: string, type?: string) =>
  `/${Paths.Exit}${addOptionalParam(surveyGroupId)}${addOptionalParam(type)}`;
const createArchivedOnboardingPath = (surveyGroupId?: string, type?: string) =>
  `/${Paths.ArchivedOnboarding}${addOptionalParam(surveyGroupId)}${addOptionalParam(type)}`;
const createProcessSurveysPath = () => `/${Paths.ProcessSurveys}`;
const createProcessSurveyDetailPath = (surveyGroupId: string, type?: string) =>
  `/${Paths.ProcessSurveyDetail}/${surveyGroupId}${addOptionalParam(type)}`;
const createArchivedProcessSurveyDetailPath = (surveyGroupId: string, type?: string) =>
  `/${Paths.ArchivedProcessSurveyDetail}/${surveyGroupId}${addOptionalParam(type)}`;

/**
 * Survey groups
 */
const createCreateOnboardingPath = (processTopicGroupId: string) => `/${Paths.CreateOnboarding}/${processTopicGroupId}`;
const createCreateExitPath = (processTopicGroupId: string) => `/${Paths.CreateExit}/${processTopicGroupId}`;
const createProcessSurveyPath = (processTopicGroupId: string) => `/${Paths.CreateProcessSurvey}/${processTopicGroupId}`;

/**
 * Reports
 */
const createReportDetailPath = (id: string) => `/${Paths.ReportDetail}/${id}`;

/**
 * SurveyArchive
 */
const createSurveyArchivePath = () => `/${Paths.SurveyArchive}`;

/**
 * Process
 */
const createProcessStepPath = (processTopicGroupId: string, stepId: string, languageId?: string) =>
  `/${Paths.Process}/${processTopicGroupId}/step/${stepId}/${languageId}`;
const createProcessStepEditPath = (processTopicGroupId) => `/stepsEdit/${processTopicGroupId}`;
const createAddStepToProcessPath = (processTopicGroupId: string, topicId: string) =>
  `/${Paths.Process}/${processTopicGroupId}/step/add/${topicId}`;
const createProcessDetail = (processTopicGroupId: string) => `/${Paths.Process}/${processTopicGroupId}`;

/**
 * Topics
 */
const createConversationSubjectsPath = (type?: string) => `/${Paths.ConversationSubjects}/${type ? type : ''}`;

/**
 * Overview
 */
const createOverviewPath = () => `/${Paths.Overview}`;

/**
 * Topic editor
 */
const createTopicEditorPath = (topicGroupId: string, languageId?: string, topicId?: string) =>
  `/${Paths.TopicEditor}/${topicGroupId}${addOptionalParam(languageId)}` + (topicId ? `?topicId=${topicId}` : '');
const createTopicEditorPreviewPath = (topicGroupId: string, languageId?: string, topicId?: string) =>
  `/${Paths.TopicEditorPreview}/${topicGroupId}${addOptionalParam(languageId)}` +
  (topicId ? `?topicId=${topicId}` : '');

const createTransactionHistoryPath = () => `/${Paths.Settings}/${Paths.TransactionHistory}`;
const createSettingsPath = () => `/${Paths.Settings}`;

export const usePath = () => {
  const history = useHistory();

  return {
    surveyDetailPath: pathCreator(createSurveyDetailPath, history.push),
    reportsSettingPath: pathCreator(createReportsSettingPath, history.push),
    surveysPath: pathCreator(createSurveysPath, history.push),
    pulserSurveysPath: pathCreator(createPulserSurveysPath, history.push),
    pulserConversationSubjectsPath: pathCreator(createPulserConversationSubjectsPath, history.push),
    pulserTopicsPath: pathCreator(createPulserTopicsPath, history.push),
    createSurveyPath: pathCreator(crateCreateSurveyPath, history.push),
    reopenSurveyPath: pathCreator(createReopenSurveyPath, history.push),

    createOnboardingPath: pathCreator(createCreateOnboardingPath, history.push),
    createProcessSurveyPath: pathCreator(createProcessSurveyPath, history.push),
    createExitPath: pathCreator(createCreateExitPath, history.push),

    reportDetailPath: pathCreator(createReportDetailPath, history.push),

    surveyArchivePath: pathCreator(createSurveyArchivePath, history.push),

    processStepEditPath: pathCreator(createProcessStepEditPath, history.push),
    processStepPath: pathCreator(createProcessStepPath, history.push),
    addStepToProcessPath: pathCreator(createAddStepToProcessPath, history.push),

    conversationSubjectsPath: pathCreator(createConversationSubjectsPath, history.push),

    overviewPath: pathCreator(createOverviewPath, history.push),
    onboardingPath: pathCreator(createOnboardingPath, history.push),
    exitPath: pathCreator(createExitPath, history.push),
    processSurveysPath: pathCreator(createProcessSurveysPath, history.push),
    processSurveyDetailPath: pathCreator(createProcessSurveyDetailPath, history.push),
    topicEditor: pathCreator(createTopicEditorPath, history.push),
    topicEditorPreview: pathCreator(createTopicEditorPreviewPath, history.push),
    archivedSurveyDetailPath: pathCreator(createArchivedSurveyDetailPath, history.push),
    archivedOnboardingPath: pathCreator(createArchivedOnboardingPath, history.push),
    archivedProcessSurveyDetailPath: pathCreator(createArchivedProcessSurveyDetailPath, history.push),
    processDetailPath: pathCreator(createProcessDetail, history.push),
    transactionHistoryPath: pathCreator(createTransactionHistoryPath, history.push),
    settingsPath: pathCreator(createSettingsPath, history.push),
  };
};
